import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Alert, Badge, Card, DataTable, EmptyState, Pagination, RefreshingIndicator, SkeletonTable } from '@/components/ui'
import type { Column } from '@/components/ui'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { formatBaht } from '@/lib/money'
import { useSubscriptionInvoices } from '@/hooks/useSubscription'
import type { SubscriptionInvoice, SubscriptionInvoiceStatus } from '@/hooks/useSubscription'
import { useBillingPeriodLabel } from './periodLabel'

function statusVariant(status: SubscriptionInvoiceStatus) {
  if (status === 'paid') return 'success' as const
  if (status === 'pending') return 'warning' as const
  return 'ghost' as const
}

/**
 * EP-144 T144-008: the account's platform invoices, newest period first.
 *
 * Read-only: invoices are written by the monthly cron and settled by a super
 * admin through the admin plane (Q-5), so there is no action column. A row
 * opens the detail page, which is the only surface carrying the engine lines
 * and the payment. Totals
 * arrive as decimal strings and are coerced for formatting only, never
 * recomputed. Zero invoices is the ordinary state of every account before its
 * first cron run, so it renders an EmptyState rather than a spinner (BA-30).
 */
export function SubscriptionInvoiceList() {
  const { t } = useTranslation('settings')
  const formatDate = useDateFormatter()
  const periodLabel = useBillingPeriodLabel()
  const navigate = useNavigate()
  const [page, setPage] = useState(1)

  const detailPath = (invoice: SubscriptionInvoice) =>
    `/settings/subscription/invoices/${invoice.id}`
  const { data, isLoading, isFetching, isError } = useSubscriptionInvoices(page)

  const invoices = data?.data ?? []
  const meta = data?.meta

  const columns: Column<SubscriptionInvoice>[] = [
    {
      key: 'number',
      header: t('subscription.invoices.columns.number'),
      // A Link, not plain text: onRowClick is a mouse-only affordance, so
      // without this cell the detail page is unreachable from a keyboard.
      render: (invoice) => (
        <Link to={detailPath(invoice)} className="link link-hover font-medium break-all">
          {invoice.number}
        </Link>
      ),
    },
    {
      key: 'period',
      header: t('subscription.invoices.columns.period'),
      hideBelow: 'sm',
      render: (invoice) => periodLabel(invoice.period_from),
    },
    {
      key: 'room_count',
      header: t('subscription.invoices.columns.rooms'),
      hideBelow: 'md',
      render: (invoice) => <span className="tabular-nums">{invoice.room_count}</span>,
    },
    {
      key: 'total',
      header: t('subscription.invoices.columns.total'),
      render: (invoice) => <span className="tabular-nums">{formatBaht(Number(invoice.total))}</span>,
    },
    {
      key: 'due_at',
      header: t('subscription.invoices.columns.due_at'),
      hideBelow: 'sm',
      render: (invoice) => formatDate(invoice.due_at),
    },
    {
      key: 'status',
      header: t('subscription.invoices.columns.status'),
      render: (invoice) => (
        <Badge variant={statusVariant(invoice.status)}>
          {t(`subscription.invoices.status.${invoice.status}`)}
        </Badge>
      ),
    },
  ]

  return (
    <Card
      title={t('subscription.invoices.title')}
      className="mt-6"
      actions={<RefreshingIndicator isFetching={isFetching} isLoading={isLoading} />}
    >
      {isLoading && <SkeletonTable rows={3} />}

      {!isLoading && isError && (
        <Alert variant="error">{t('subscription.errors.load_failed')}</Alert>
      )}

      {!isLoading && !isError && invoices.length === 0 && (
        <EmptyState
          title={t('subscription.invoices.empty')}
          description={t('subscription.invoices.empty_description')}
        />
      )}

      {!isLoading && !isError && invoices.length > 0 && (
        <>
          {/* EP-145 D11: one table at every width. This page used to render the
              same six columns twice, a DataTable above sm and a hand-rolled
              card stack below it, and it was the only DataTable page in the app
              doing so. The narrow viewport is handled the way the other
              nineteen handle it, by dropping columns with hideBelow. */}
          <DataTable<SubscriptionInvoice>
            columns={columns}
            data={invoices}
            keyExtractor={(invoice) => invoice.id}
            size="sm"
            emptyMessage={t('subscription.invoices.empty')}
            onRowClick={(invoice) => navigate(detailPath(invoice))}
          />

          {meta && (
            /* stickyOnMobile is opted out here (S-5): the default pins a
               fixed bar to the bottom of the WHOLE Settings page, which would
               hover over the tier card too. This is a card inside a tab, not a
               list page. */
            <Pagination
              currentPage={meta.current_page}
              totalPages={meta.total_pages}
              onPageChange={setPage}
              stickyOnMobile={false}
            />
          )}
        </>
      )}
    </Card>
  )
}
